import {
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
  type ReactNode,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";
import { ChevronDown, X } from "lucide-react";

import { registerViewportLayoutListeners } from "@/lib/useViewportLayoutEvents";
import { cn } from "@/lib/utils";
import { shouldIgnoreOpeningPointer } from "@/lib/touchOpenClose";
import { useTouchListPickSession } from "@/lib/useTouchListPickSession";
import {
  computeAnchoredListPanel,
  FLOATING_POPOVER_Z_INDEX,
  type AnchoredListPanelBox,
} from "@/lib/viewportAnchoredPopover";
import { Input } from "@/components/ui/Input";
import { FLOOR_PAGE_SCAN_TARGET_PROPS } from "@/lib/floorPageScanCapture";

export interface SearchableSelectOption {
  value: string;
  label: string;
  /** Secondary line in the dropdown (for example operator tips). Not shown on the closed trigger. */
  description?: string | null;
  /** Non-interactive row (for example a section heading between option groups). */
  disabled?: boolean;
  kind?: "option" | "divider";
  className?: string;
}

export function isSearchableSelectDivider(option: SearchableSelectOption): boolean {
  return option.kind === "divider";
}

export function searchableSelectSelectableOptions(
  options: readonly SearchableSelectOption[],
): SearchableSelectOption[] {
  return options.filter((option) => option.disabled !== true && !isSearchableSelectDivider(option));
}

export function SearchableSelectDividerRow({ label }: { label?: string }): JSX.Element {
  return (
    <li role="separator" className="list-none px-2 py-1" aria-hidden>
      {label?.trim() ? <div className="px-1 pb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</div> : null}
      <div className="border-t-2 border-slate-300" />
    </li>
  );
}

export type SearchableSelectPresentation = "panelSearch" | "inlineFilter";

export interface SearchableSelectProps {
  id?: string;
  value: string;
  onValueChange: (option: SearchableSelectOption | null) => void;
  options: SearchableSelectOption[];
  searchValue: string;
  onSearchChange: (query: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  isLoading?: boolean;
  disabled?: boolean;
  /**
   * `panelSearch` — button trigger and a separate search field inside the dropdown (default).
   * Use for required entity pickers (supplier, vendor, work order, and similar).
   * `inlineFilter` — one text field filters the list; for filter rails and similar controls where
   * typing narrows a list without committing identity until blur resolves a unique match.
   */
  presentation?: SearchableSelectPresentation;
  /** When true, opens the list on mount so the filter field is focused (for example in a modal). `panelSearch` only. */
  initialOpen?: boolean;
  /** When `presentation` is `inlineFilter`, focuses the input on mount unless disabled. */
  autoFocus?: boolean;
  /** Shown on the trigger when `value` is set but the option is not in the current `options` list (for example after a server filter). */
  selectedLabelOverride?: string | null;
  className?: string;
  /** Merged onto the panel trigger button (`presentation` `panelSearch` only). Use for example `h-9` to match dense filter rails. */
  triggerClassName?: string;
  /** Minimum width of the portaled dropdown panel (defaults to 120px). Use when labels are longer than the trigger. */
  panelMinWidth?: number;
  /** Optional trailing content on each dropdown option row (for example status badges). */
  optionTrailing?: (option: SearchableSelectOption) => ReactNode;
  /** Optional trailing content beside the committed selected value (closed trigger / inline filter). */
  selectedValueTrailing?: () => ReactNode;
  /** When true, marks the search input as the floor page HID scan target. */
  floorScanTarget?: boolean;
  /** Called before default keyboard handling on the dropdown search field. */
  onSearchInputKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  /**
   * Inline filter only. Enter or Tab commits the highlighted row, or the first
   * visible match when nothing has been highlighted yet.
   */
  commitFirstFilteredOption?: boolean;
  /** Optional ref to the visible search input (inline filter field or panel search field). */
  searchInputRef?: RefObject<HTMLInputElement | null>;
  /**
   * Sticky action under the portaled list (`panelSearch` only). Stays visible while the
   * option list scrolls. `close` dismisses the panel after the action commits.
   */
  panelFooter?: (controls: { close: () => void }) => ReactNode;
  /**
   * When false, `options` are shown as given. Use when the parent already filtered them
   * (for example a server search) so a second local match cannot hide those rows.
   */
  filterLocally?: boolean;
  /**
   * Inline filter only. When false, focusing a committed value does not copy its label
   * into the search query. Use with server search so the label is not sent as the query.
   */
  seedCommittedLabelOnFocus?: boolean;
  /**
   * Shows an X inside the field when a value is committed. Choosing it calls `onValueChange(null)`.
   */
  clearable?: boolean;
  /** Accessible name for the inline clear control. */
  clearLabel?: string;
}

function mergeSearchInputRef(
  internalRef: MutableRefObject<HTMLInputElement | null>,
  externalRef?: RefObject<HTMLInputElement | null>,
): (element: HTMLInputElement | null) => void {
  return (element) => {
    internalRef.current = element;
    if (externalRef != null) {
      externalRef.current = element;
    }
  };
}

function isSearchableSelectOptionSelectable(option: SearchableSelectOption): boolean {
  return option.disabled !== true && !isSearchableSelectDivider(option);
}

function stopWheelFromClosingSelectPanel(event: React.WheelEvent): void {
  event.stopPropagation();
}

function selectableSearchableSelectOptions(options: SearchableSelectOption[]): SearchableSelectOption[] {
  return options.filter(isSearchableSelectOptionSelectable);
}

/** Tall enough for two-line option rows (label + description) without excessive viewport use. */
const LIST_MAX_HEIGHT_PX = 320;
const PANEL_GAP_PX = 4;
const VIEWPORT_EDGE_PX = 8;
/** Search field block inside the panel (padding plus input). */
const PANEL_SEARCH_CHROME_PX = 48;
/** Footer action under the list (padding plus a touch-height button). */
const PANEL_FOOTER_CHROME_PX = 68;

/** When the panel search field has a single match or an exact label/value hit, commit that option (Enter key). */
export function pickSearchableSelectOptionFromSearch(
  filteredOptions: SearchableSelectOption[],
  searchValue: string,
): SearchableSelectOption | null {
  const needleRaw = searchValue.trim();
  if (needleRaw.length === 0) return null;

  const selectable = selectableSearchableSelectOptions(filteredOptions);
  if (selectable.length === 1) {
    return selectable[0];
  }

  const exactLabel = selectable.filter(
    (option) => option.label.trim().toLowerCase() === needleRaw.toLowerCase(),
  );
  if (exactLabel.length === 1) return exactLabel[0];

  const byValue = selectable.filter((option) => option.value === needleRaw);
  if (byValue.length === 1) return byValue[0];

  return null;
}

/** Enter or Tab: highlighted row, otherwise the first visible match when that mode is on. */
export function confirmSearchableSelectOption(
  selectableOptions: readonly SearchableSelectOption[],
  highlightedIndex: number,
  searchValue: string,
  commitFirstFilteredOption: boolean,
): SearchableSelectOption | null {
  if (searchValue.trim() === "") return null;
  if (highlightedIndex >= 0 && highlightedIndex < selectableOptions.length) {
    return selectableOptions[highlightedIndex] ?? null;
  }
  if (commitFirstFilteredOption) {
    return selectableOptions[0] ?? null;
  }
  return pickSearchableSelectOptionFromSearch([...selectableOptions], searchValue);
}

/** Filter options for inline/panel search fields (label, value, description). */
function inlineFilterShouldOpenList(searchValue: string, optionsLength: number): boolean {
  if (searchValue.trim().length > 0) {
    return true;
  }
  return optionsLength > 0;
}

export function filterSearchableSelectOptions(
  options: SearchableSelectOption[],
  searchValue: string,
): SearchableSelectOption[] {
  const needle = searchValue.trim().toLowerCase();
  if (needle.length === 0) {
    return options;
  }
  const filtered = options.filter((option) => {
    if (option.disabled) {
      return false;
    }
    const label = option.label.toLowerCase();
    const val = option.value.toLowerCase();
    const desc = (option.description ?? "").toLowerCase();
    return label.includes(needle) || val.includes(needle) || desc.includes(needle);
  });
  return filtered
    .map((option, index) => ({
      option,
      index,
      exactRank: searchableSelectMatchRank(option, needle),
    }))
    .sort((left, right) => left.exactRank - right.exactRank || left.option.value.length - right.option.value.length || left.index - right.index)
    .map(({ option }) => option);
}

function searchableSelectMatchRank(option: SearchableSelectOption, needle: string): number {
  const value = option.value.trim().toLowerCase();
  const label = option.label.trim().toLowerCase();
  if (value === needle || label === needle) return 0;
  if (value.endsWith(needle)) return 1;
  if (value.includes(needle)) return 2;
  if (label.includes(needle)) return 3;
  return 4;
}

function searchableSelectOptionDomId(listboxId: string, optionIndex: number): string {
  return `${listboxId}-option-${String(optionIndex)}`;
}

export function nextSearchableSelectHighlightIndex(
  optionCount: number,
  currentIndex: number,
  direction: "next" | "previous",
): number {
  if (optionCount <= 0) return -1;
  if (currentIndex < 0) {
    return direction === "next" ? 0 : optionCount - 1;
  }
  return direction === "next"
    ? Math.min(currentIndex + 1, optionCount - 1)
    : Math.max(currentIndex - 1, 0);
}

/** Closed-trigger label: explicit override wins over a placeholder option row in the current list. */
export function resolveSearchableSelectDisplayLabel(
  value: string,
  selectedFromList: SearchableSelectOption | undefined,
  selectedLabelOverride: string | null,
): string | undefined {
  if (!value) return undefined;
  const override = selectedLabelOverride?.trim();
  if (override && override.length > 0) {
    return override;
  }
  if (selectedFromList?.label) {
    return selectedFromList.label;
  }
  return `Option ${value}`;
}

export type ResolveInlineFilterBlurInput = {
  searchValue: string;
  filteredOptions: SearchableSelectOption[];
  committedValue: string;
  committedLabel: string | null;
  highlightedOption?: SearchableSelectOption | null;
};

export type ResolveInlineFilterBlurResult =
  | { action: "commit"; option: SearchableSelectOption }
  | { action: "keep"; restoreSearch: string }
  | { action: "clear" };

/**
 * Inline-filter blur resolution: unique partial/exact match commits; ambiguous text with an
 * existing committed value keeps the selection and restores its label.
 */
export function resolveInlineFilterBlurCommit(
  input: ResolveInlineFilterBlurInput,
): ResolveInlineFilterBlurResult {
  if (input.highlightedOption != null) {
    return { action: "commit", option: input.highlightedOption };
  }
  const needleRaw = input.searchValue.trim();
  if (needleRaw.length === 0) {
    if (input.committedValue.trim().length === 0) {
      return { action: "clear" };
    }
    const restoreSearch =
      input.committedLabel && input.committedLabel.trim().length > 0
        ? input.committedLabel
        : `Option ${input.committedValue}`;
    return { action: "keep", restoreSearch };
  }

  const picked = pickSearchableSelectOptionFromSearch(input.filteredOptions, needleRaw);
  if (picked != null) {
    return { action: "commit", option: picked };
  }

  if (input.committedValue.trim().length > 0) {
    const restoreSearch =
      input.committedLabel && input.committedLabel.trim().length > 0
        ? input.committedLabel
        : `Option ${input.committedValue}`;
    return { action: "keep", restoreSearch };
  }

  return { action: "clear" };
}

/** Inline field text: a committed row shows its label until the person types again. */
export function resolveInlineFilterInputDisplay(input: {
  editingQuery: boolean;
  searchValue: string;
  committedValue: string;
  committedLabel: string | null;
  pendingLabel?: string | null;
}): string {
  if (input.editingQuery) {
    return input.searchValue;
  }
  const pending = input.pendingLabel?.trim() ?? "";
  const label = input.committedLabel?.trim() ?? "";
  if (input.committedValue.trim() !== "" && label !== "") {
    return label;
  }
  if (pending !== "") {
    return pending;
  }
  return input.searchValue;
}

function getScrollParents(node: HTMLElement | null): HTMLElement[] {
  const parents: HTMLElement[] = [];
  let el = node?.parentElement;
  while (el) {
    const style = window.getComputedStyle(el);
    const oy = style.overflowY;
    const ox = style.overflowX;
    const o = style.overflow;
    if (/(auto|scroll|overlay)/.test(oy) || /(auto|scroll|overlay)/.test(ox) || /(auto|scroll|overlay)/.test(o)) {
      parents.push(el);
    }
    el = el.parentElement;
  }
  return parents;
}

function SearchableSelectInlineFilter({
  id: providedId,
  value,
  onValueChange,
  options,
  searchValue,
  onSearchChange,
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  emptyMessage = "No matches.",
  isLoading = false,
  disabled = false,
  initialOpen: _ignoredInitialOpen = false,
  selectedLabelOverride = null,
  className,
  triggerClassName: _ignoredTriggerClassName,
  panelMinWidth = 120,
  autoFocus = false,
  optionTrailing,
  selectedValueTrailing,
  floorScanTarget = false,
  onSearchInputKeyDown,
  searchInputRef,
  filterLocally = true,
  seedCommittedLabelOnFocus = true,
  commitFirstFilteredOption = false,
  clearable = false,
  clearLabel = "Clear selection",
}: Omit<SearchableSelectProps, "presentation">) {
  void _ignoredTriggerClassName;
  void _ignoredInitialOpen;
  const generatedId = useId();
  const inputId = providedId ?? `${generatedId}-inline-filter`;
  const listboxId = `${inputId}-listbox`;

  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const setInputRef = mergeSearchInputRef(inputRef, searchInputRef);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const openGuardRef = useRef<{ openedAtMs: number; pointerId: number | null }>({
    openedAtMs: 0,
    pointerId: null,
  });
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [editingQuery, setEditingQuery] = useState(false);
  const [pendingLabel, setPendingLabel] = useState<string | null>(null);
  const ignoreSearchEchoUntilRef = useRef(0);
  const [highlightedValue, setHighlightedValue] = useState<string | null>(null);
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const [panelBox, setPanelBox] = useState<{
    top: number;
    left: number;
    width: number;
    listContentMaxHeight: number;
  } | null>(null);
  const touchListPick = useTouchListPickSession(open, {
    onGestureEnd: () => setFocused(false),
  });

  const selectedFromList = value ? options.find((option) => option.value === value) : undefined;
  const resolvedLabelWhenClosed = resolveSearchableSelectDisplayLabel(
    value,
    selectedFromList,
    selectedLabelOverride,
  );

  const realCommittedLabel =
    selectedFromList?.label
    ?? (selectedLabelOverride && selectedLabelOverride.trim().length > 0 ? selectedLabelOverride : null);
  const inputDisplay = resolveInlineFilterInputDisplay({
    editingQuery,
    searchValue,
    committedValue: value,
    committedLabel: realCommittedLabel,
    pendingLabel,
  });

  const updatePanelPosition = (): void => {
    const anchor = inputRef.current;
    if (!anchor) {
      return;
    }
    const box = computeAnchoredListPanel({
      anchorRect: anchor.getBoundingClientRect(),
      gap: PANEL_GAP_PX,
      edge: VIEWPORT_EDGE_PX,
      minWidth: panelMinWidth,
      chromeHeight: 0,
      listMaxHeightCap: LIST_MAX_HEIGHT_PX,
    });
    setPanelBox({
      top: box.top,
      left: box.left,
      width: box.width,
      listContentMaxHeight: box.listContentMaxHeight,
    });
  };

  useLayoutEffect(() => {
    if (!open) {
      setPanelBox(null);
      return;
    }
    updatePanelPosition();
    const onWin = (): void => {
      updatePanelPosition();
    };
    const removeViewport = registerViewportLayoutListeners(onWin);
    const scrollParents = getScrollParents(inputRef.current);
    for (const parent of scrollParents) {
      parent.addEventListener("scroll", onWin, { passive: true });
    }
    return () => {
      removeViewport();
      for (const parent of scrollParents) {
        parent.removeEventListener("scroll", onWin);
      }
    };
  }, [open, options.length, searchValue, inputDisplay]);

  useEffect(() => {
    if (!open) return;

    const dismissIfOutside = (target: EventTarget | null): void => {
      if (!(target instanceof Node)) {
        return;
      }
      if (containerRef.current?.contains(target) || panelRef.current?.contains(target)) {
        return;
      }
      setOpen(false);
    };

    const onPointerDown = (event: PointerEvent) => {
      if (
        shouldIgnoreOpeningPointer({
          openedAtMs: openGuardRef.current.openedAtMs,
          openingPointerId: openGuardRef.current.pointerId,
          nowMs: Date.now(),
          eventPointerId: event.pointerId,
          eventPointerType: event.pointerType,
        })
      ) {
        return;
      }
      dismissIfOutside(event.target);
    };
    const onFocusIn = (event: FocusEvent) => {
      dismissIfOutside(event.target);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("focusin", onFocusIn, true);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("focusin", onFocusIn, true);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  function seedSearchFromCommittedLabel(): void {
    if (!value) return;
    if (searchValue.trim() !== "") return;
    const label =
      selectedFromList?.label ??
      (selectedLabelOverride && selectedLabelOverride.length > 0 ? selectedLabelOverride : null);
    const seed =
      typeof label === "string" && label.trim().length > 0 ? label : (`Option ${value}` as string);
    onSearchChange(seed);
  }

  function effectiveInlineFilterSearchForOpen(): string {
    const trimmed = searchValue.trim();
    if (trimmed.length > 0) {
      return searchValue;
    }
    if (!value) {
      return searchValue;
    }
    const label =
      selectedFromList?.label ??
      (selectedLabelOverride && selectedLabelOverride.length > 0 ? selectedLabelOverride : null);
    if (typeof label === "string" && label.trim().length > 0) {
      return label;
    }
    return searchValue;
  }

  const filteredOptions = useMemo(
    () => (filterLocally ? filterSearchableSelectOptions(options, searchValue) : options),
    [filterLocally, options, searchValue],
  );
  const selectableFilteredOptions = useMemo(
    () => selectableSearchableSelectOptions(filteredOptions),
    [filteredOptions],
  );
  const highlightedIndex = highlightedValue == null
    ? -1
    : selectableFilteredOptions.findIndex((option) => option.value === highlightedValue);
  const effectiveHighlightedIndex =
    commitFirstFilteredOption && highlightedIndex < 0 && selectableFilteredOptions.length > 0
      ? 0
      : highlightedIndex;

  useEffect(() => {
    if (highlightedValue != null && highlightedIndex < 0) {
      setHighlightedValue(null);
    }
  }, [highlightedIndex, highlightedValue]);

  function highlightInlineOption(nextIndex: number): void {
    const option = selectableFilteredOptions[nextIndex];
    if (option == null) return;
    setHighlightedValue(option.value);
    window.requestAnimationFrame(() => {
      optionRefs.current[nextIndex]?.scrollIntoView({ block: "nearest" });
    });
  }

  function commitInlineOption(option: SearchableSelectOption): void {
    ignoreSearchEchoUntilRef.current = Date.now() + 400;
    setEditingQuery(false);
    setFocused(false);
    setPendingLabel(option.label);
    onSearchChange("");
    onValueChange(option);
    setHighlightedValue(null);
    setOpen(false);
    inputRef.current?.blur();
  }

  function commitInlineBlur(): void {
    const highlightedOption =
      effectiveHighlightedIndex >= 0 ? selectableFilteredOptions[effectiveHighlightedIndex] : null;
    const committedLabel =
      selectedFromList?.label ??
      (selectedLabelOverride && selectedLabelOverride.length > 0 ? selectedLabelOverride : null);
    const result = resolveInlineFilterBlurCommit({
      searchValue,
      filteredOptions,
      committedValue: value,
      committedLabel,
      highlightedOption,
    });
    if (result.action === "commit") {
      commitInlineOption(result.option);
      return;
    }
    if (result.action === "keep") {
      setEditingQuery(false);
      setOpen(false);
      return;
    }
    if (pendingLabel != null && pendingLabel.trim() !== "") {
      setEditingQuery(false);
      setOpen(false);
      return;
    }
    setEditingQuery(false);
    setPendingLabel(null);
    onValueChange(null);
    onSearchChange("");
    setOpen(false);
  }

  const portalTarget = typeof document !== "undefined" ? document.body : null;

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      <div className="relative">
        <Input
          ref={setInputRef}
          id={inputId}
          type="text"
          disabled={disabled}
          autoComplete="off"
          autoFocus={autoFocus && !disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={open ? listboxId : undefined}
          aria-activedescendant={
            open && effectiveHighlightedIndex >= 0
              ? searchableSelectOptionDomId(listboxId, effectiveHighlightedIndex)
              : undefined
          }
          role="combobox"
          value={inputDisplay}
          placeholder={placeholder}
          aria-label={searchPlaceholder}
          className={cn(
            "h-9 bg-white pr-9 text-sm disabled:cursor-not-allowed disabled:bg-slate-100",
            clearable && (value || pendingLabel || searchValue.trim() !== "") ? "pr-14" : undefined,
            !focused && value && resolvedLabelWhenClosed ? "border-slate-300" : undefined,
          )}
          onPointerDown={(event) => {
            if (disabled) return;
            openGuardRef.current = { openedAtMs: Date.now(), pointerId: event.pointerId };
          }}
          onFocus={() => {
            if (disabled) return;
            setFocused(true);
            if (seedCommittedLabelOnFocus) {
              seedSearchFromCommittedLabel();
            }
            if (inlineFilterShouldOpenList(effectiveInlineFilterSearchForOpen(), options.length)) {
              setOpen(true);
            }
          }}
          onChange={(event) => {
            if (disabled) return;
            const next = event.target.value;
            if (Date.now() < ignoreSearchEchoUntilRef.current) {
              return;
            }
            setEditingQuery(true);
            setPendingLabel(null);
            setHighlightedValue(null);
            onSearchChange(next);
            setOpen(inlineFilterShouldOpenList(next, options.length));
          }}
          onBlur={(event) => {
            const nextFocusedElement = event.relatedTarget;
            if (
              nextFocusedElement instanceof Node
              && panelRef.current?.contains(nextFocusedElement)
            ) {
              return;
            }
            window.setTimeout(() => {
              // A tablet tap blurs the field on pointer down, before pointer up commits the row.
              // Resolving that blur would close the list and clear an ambiguous search.
              if (touchListPick.shouldYieldBlurCommit()) {
                if (!touchListPick.isPickPending()) {
                  setFocused(false);
                }
                return;
              }
              setFocused(false);
              commitInlineBlur();
            }, 0);
          }}
          onKeyDown={(event) => {
            onSearchInputKeyDown?.(event);
            if (event.defaultPrevented) return;
            if (event.key === "Escape") {
              event.stopPropagation();
              setHighlightedValue(null);
              setOpen(false);
              inputRef.current?.blur();
            }
            if (event.key === "ArrowDown") {
              event.preventDefault();
              setOpen(true);
              highlightInlineOption(
                nextSearchableSelectHighlightIndex(
                  selectableFilteredOptions.length,
                  effectiveHighlightedIndex,
                  "next",
                ),
              );
              return;
            }
            if (event.key === "ArrowUp") {
              event.preventDefault();
              setOpen(true);
              highlightInlineOption(
                nextSearchableSelectHighlightIndex(
                  selectableFilteredOptions.length,
                  effectiveHighlightedIndex,
                  "previous",
                ),
              );
              return;
            }
            if (event.key === "Enter" || (event.key === "Tab" && commitFirstFilteredOption)) {
              if (isLoading && commitFirstFilteredOption && selectableFilteredOptions.length === 0) {
                return;
              }
              event.stopPropagation();
              const picked = confirmSearchableSelectOption(
                selectableFilteredOptions,
                effectiveHighlightedIndex,
                searchValue,
                commitFirstFilteredOption,
              );
              if (picked != null) {
                event.preventDefault();
                commitInlineOption(picked);
              }
            }
          }}
          {...(floorScanTarget ? FLOOR_PAGE_SCAN_TARGET_PROPS : {})}
        />
        {!disabled && clearable && (value || pendingLabel || searchValue.trim() !== "") ? (
          <button
            type="button"
            aria-label={clearLabel}
            className="absolute right-8 top-1/2 -translate-y-1/2 rounded p-0.5 text-slate-500 hover:bg-slate-100 hover:text-slate-800"
            onMouseDown={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              setEditingQuery(false);
              setPendingLabel(null);
              onSearchChange("");
              onValueChange(null);
              setHighlightedValue(null);
              setOpen(false);
            }}
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        ) : null}
        {!disabled ? (
          <ChevronDown
            className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500"
            aria-hidden
          />
        ) : null}
        {!focused && value && selectedValueTrailing ? (
          <div className="pointer-events-auto absolute right-8 top-1/2 -translate-y-1/2">
            {selectedValueTrailing()}
          </div>
        ) : null}
      </div>
      {open && panelBox != null && portalTarget
        ? createPortal(
            <div
              ref={panelRef}
              id={listboxId}
              role="listbox"
              data-searchable-select-panel
              style={{
                position: "fixed",
                top: panelBox.top,
                left: panelBox.left,
                width: panelBox.width,
                zIndex: FLOATING_POPOVER_Z_INDEX,
              }}
              className="overflow-hidden rounded-md border border-slate-200 bg-white shadow-lg"
              onWheel={stopWheelFromClosingSelectPanel}
            >
              <ul
                className="overflow-auto overscroll-y-contain py-1 text-sm touch-pan-y [-webkit-overflow-scrolling:touch]"
                style={{ maxHeight: panelBox.listContentMaxHeight }}
                onWheel={stopWheelFromClosingSelectPanel}
              >
                {isLoading ? (
                  <li className="px-3 py-2 text-slate-500">Loading…</li>
                ) : options.length === 0 ? (
                  <li className="px-3 py-2 text-slate-500">{emptyMessage}</li>
                ) : filteredOptions.length === 0 ? (
                  <li className="px-3 py-2 text-slate-500">{emptyMessage}</li>
                ) : (
                  filteredOptions.map((option) => {
                    if (isSearchableSelectDivider(option)) {
                      return <SearchableSelectDividerRow key={option.value} label={option.label} />;
                    }
                      if (option.disabled) {
                        return (
                          <li
                            key={option.value}
                            role="presentation"
                            className="border-t border-slate-200 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-400"
                          >
                            {option.label}
                          </li>
                        );
                      }
                      const isSelected = option.value === value;
                      const selectableIndex = selectableFilteredOptions.findIndex(
                        (candidate) => candidate.value === option.value,
                      );
                      const isHighlighted =
                        (highlightedValue != null && option.value === highlightedValue)
                        || (
                          commitFirstFilteredOption
                          && highlightedValue == null
                          && selectableIndex === 0
                        );
                      return (
                        <li key={option.value} role="presentation">
                          <button
                            ref={(element) => {
                              if (selectableIndex >= 0) {
                                optionRefs.current[selectableIndex] = element;
                              }
                            }}
                            id={
                              selectableIndex >= 0
                                ? searchableSelectOptionDomId(listboxId, selectableIndex)
                                : undefined
                            }
                            type="button"
                            role="option"
                            aria-selected={isSelected}
                            className={cn(
                              "flex min-h-11 w-full items-center justify-between gap-2 px-3 py-2 text-left touch-manipulation hover:bg-slate-100",
                              isSelected && "bg-slate-50 font-medium",
                              isHighlighted && "bg-slate-100",
                            )}
                            onPointerMove={() => setHighlightedValue(option.value)}
                            onPointerDown={(event) => {
                              if (disabled) return;
                              touchListPick.onListItemPointerDown(event, () => {
                                commitInlineOption(option);
                              });
                            }}
                          >
                            <span className="flex min-w-0 flex-col gap-0.5 text-left">
                            <span className={cn("whitespace-nowrap", option.className)}>{option.label}</span>
                            {option.description ? (
                              <span className="whitespace-normal text-xs font-normal leading-snug text-slate-500">
                                {option.description}
                              </span>
                            ) : null}
                          </span>
                          {optionTrailing ? (
                            <span className="shrink-0 self-start">{optionTrailing(option)}</span>
                          ) : null}
                        </button>
                      </li>
                    );
                  })
                )}
              </ul>
            </div>,
            portalTarget,
          )
        : null}
    </div>
  );
}

function SearchableSelectPanelSearch({
  id: providedId,
  value,
  onValueChange,
  options,
  searchValue,
  onSearchChange,
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  emptyMessage = "No matches.",
  isLoading = false,
  disabled = false,
  initialOpen = false,
  selectedLabelOverride = null,
  className,
  triggerClassName,
  panelMinWidth = 120,
  optionTrailing,
  selectedValueTrailing,
  floorScanTarget = false,
  onSearchInputKeyDown,
  searchInputRef,
  filterLocally = true,
  seedCommittedLabelOnFocus: _ignoredSeedCommittedLabelOnFocus = true,
  panelFooter,
  clearable = false,
  clearLabel = "Clear selection",
}: Omit<SearchableSelectProps, "presentation" | "autoFocus">) {
  void _ignoredSeedCommittedLabelOnFocus;
  const generatedId = useId();
  const triggerId = providedId ?? `${generatedId}-trigger`;
  const listboxId = providedId ? `${providedId}-listbox` : `${generatedId}-listbox`;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const panelSearchInputRef = useRef<HTMLInputElement | null>(null);
  const setPanelSearchInputRef = mergeSearchInputRef(panelSearchInputRef, searchInputRef);
  const openGuardRef = useRef<{ openedAtMs: number; pointerId: number | null }>({
    openedAtMs: 0,
    pointerId: null,
  });
  const openedByPointerRef = useRef(false);
  const typedQueryRef = useRef(searchValue);
  typedQueryRef.current = searchValue;
  const [open, setOpen] = useState(() => Boolean(initialOpen && !disabled));
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [panelBox, setPanelBox] = useState<AnchoredListPanelBox | null>(null);
  const touchListPick = useTouchListPickSession(open);

  useLayoutEffect(() => {
    if (!initialOpen || disabled) return;
    // Seed the open-ignore window so the click that mounted this select (for example a
    // modal open button) does not immediately dismiss the list via the document listener.
    openGuardRef.current = { openedAtMs: Date.now(), pointerId: null };
  }, [initialOpen, disabled]);

  const filteredOptions = useMemo(
    () => (filterLocally ? filterSearchableSelectOptions(options, searchValue) : options),
    [filterLocally, options, searchValue],
  );
  const selectableFilteredOptions = useMemo(
    () => selectableSearchableSelectOptions(filteredOptions),
    [filteredOptions],
  );

  useEffect(() => {
    setHighlightedIndex(0);
  }, [searchValue]);

  useEffect(() => {
    if (!open) return;
    document.getElementById(searchableSelectOptionDomId(listboxId, highlightedIndex))?.scrollIntoView({
      block: "nearest",
    });
  }, [highlightedIndex, listboxId, open]);

  const selectedFromList = value ? options.find((option) => option.value === value) : undefined;
  const triggerLabel = resolveSearchableSelectDisplayLabel(value, selectedFromList, selectedLabelOverride);

  const hasPanelFooter = panelFooter != null;
  const updatePanelPosition = (): void => {
    const button = triggerRef.current;
    if (!button) {
      return;
    }
    setPanelBox(
      computeAnchoredListPanel({
        anchorRect: button.getBoundingClientRect(),
        gap: PANEL_GAP_PX,
        edge: VIEWPORT_EDGE_PX,
        minWidth: panelMinWidth,
        chromeHeight: PANEL_SEARCH_CHROME_PX + (hasPanelFooter ? PANEL_FOOTER_CHROME_PX : 0),
        listMaxHeightCap: LIST_MAX_HEIGHT_PX,
      }),
    );
  };

  useLayoutEffect(() => {
    if (!open) {
      setPanelBox(null);
      return;
    }
    updatePanelPosition();
    const onWin = (): void => {
      updatePanelPosition();
    };
    const removeViewport = registerViewportLayoutListeners(onWin);
    const scrollParents = getScrollParents(triggerRef.current);
    for (const parent of scrollParents) {
      parent.addEventListener("scroll", onWin, { passive: true });
    }
    return () => {
      removeViewport();
      for (const parent of scrollParents) {
        parent.removeEventListener("scroll", onWin);
      }
    };
  }, [open, options.length, searchValue, hasPanelFooter]);

  useEffect(() => {
    if (!open) return;

    const dismissIfOutside = (target: EventTarget | null): void => {
      if (!(target instanceof Node)) {
        return;
      }
      if (containerRef.current?.contains(target) || panelRef.current?.contains(target)) {
        return;
      }
      panelSearchInputRef.current?.blur();
      triggerRef.current?.blur();
      setOpen(false);
    };

    const onPointerDown = (event: PointerEvent) => {
      if (
        shouldIgnoreOpeningPointer({
          openedAtMs: openGuardRef.current.openedAtMs,
          openingPointerId: openGuardRef.current.pointerId,
          nowMs: Date.now(),
          eventPointerId: event.pointerId,
          eventPointerType: event.pointerType,
        })
      ) {
        return;
      }
      dismissIfOutside(event.target);
    };
    const onFocusIn = (event: FocusEvent) => {
      if (
        shouldIgnoreOpeningPointer({
          openedAtMs: openGuardRef.current.openedAtMs,
          openingPointerId: openGuardRef.current.pointerId,
          nowMs: Date.now(),
          eventPointerId: -1,
        })
      ) {
        return;
      }
      dismissIfOutside(event.target);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("focusin", onFocusIn, true);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("focusin", onFocusIn, true);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const frame = requestAnimationFrame(() => {
      panelSearchInputRef.current?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [open]);

  const portalTarget = typeof document !== "undefined" ? document.body : null;

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      <button
        ref={triggerRef}
        id={triggerId}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        onPointerDown={(event) => {
          if (disabled || event.button !== 0) return;
          event.preventDefault();
          openedByPointerRef.current = true;
          setOpen((current) => {
            const next = !current;
            if (next) {
              openGuardRef.current = { openedAtMs: Date.now(), pointerId: event.pointerId };
            }
            return next;
          });
        }}
        onClick={(event) => {
          if (disabled) return;
          if (openedByPointerRef.current) {
            openedByPointerRef.current = false;
            event.preventDefault();
            return;
          }
          setOpen((current) => {
            const next = !current;
            if (next) {
              openGuardRef.current = { openedAtMs: Date.now(), pointerId: null };
            }
            return next;
          });
        }}
        onKeyDown={(event) => {
          if (disabled || event.ctrlKey || event.metaKey || event.altKey) return;
          if (event.key === "ArrowDown" || event.key === "Enter") {
            event.preventDefault();
            setOpen(true);
            return;
          }
          if (event.key.length !== 1) return;
          event.preventDefault();
          const next = open ? `${typedQueryRef.current}${event.key}` : event.key;
          typedQueryRef.current = next;
          onSearchChange(next);
          setOpen(true);
        }}
        className={cn(
          "flex h-10 w-full items-center justify-between gap-2 rounded-md border border-slate-300 bg-white px-3 text-left text-sm touch-manipulation",
          "focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500",
          "text-slate-900 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500",
          triggerClassName,
        )}
      >
        <span className={cn("flex min-w-0 flex-1 items-center gap-2 truncate", !triggerLabel && "text-slate-400")}>
          <span className="min-w-0 truncate">{triggerLabel ?? placeholder}</span>
          {value && selectedValueTrailing ? (
            <span className="shrink-0">{selectedValueTrailing()}</span>
          ) : null}
        </span>
        {clearable && value && !disabled ? (
          <span
            role="button"
            tabIndex={0}
            aria-label={clearLabel}
            className="shrink-0 rounded p-0.5 text-slate-500 hover:bg-slate-100 hover:text-slate-800"
            onPointerDown={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onSearchChange("");
              onValueChange(null);
              setOpen(false);
            }}
            onKeyDown={(event) => {
              if (event.key !== "Enter" && event.key !== " ") return;
              event.preventDefault();
              event.stopPropagation();
              onSearchChange("");
              onValueChange(null);
              setOpen(false);
            }}
          >
            <X className="h-4 w-4" aria-hidden />
          </span>
        ) : null}
        <ChevronDown className="h-4 w-4 shrink-0 text-slate-500" aria-hidden />
      </button>
      {open && panelBox != null && portalTarget
        ? createPortal(
            <div
              ref={panelRef}
              id={listboxId}
              role="listbox"
              data-searchable-select-panel
              style={{
                position: "fixed",
                top: panelBox.top,
                left: panelBox.left,
                width: panelBox.width,
                zIndex: FLOATING_POPOVER_Z_INDEX,
              }}
              className="overflow-hidden rounded-md border border-slate-200 bg-white shadow-lg"
              onWheel={stopWheelFromClosingSelectPanel}
            >
              <div className="border-b border-slate-100 p-2">
                <Input
                  ref={setPanelSearchInputRef}
                  value={searchValue}
                  onChange={(event) => onSearchChange(event.target.value)}
                  placeholder={searchPlaceholder}
                  aria-label={searchPlaceholder}
                  aria-activedescendant={
                    selectableFilteredOptions[highlightedIndex] != null
                      ? searchableSelectOptionDomId(listboxId, highlightedIndex)
                      : undefined
                  }
                  className="h-8 text-sm"
                  onKeyDown={(event) => {
                    event.stopPropagation();
                    onSearchInputKeyDown?.(event);
                    if (event.defaultPrevented) return;
                    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                      event.preventDefault();
                      setHighlightedIndex((current) =>
                        nextSearchableSelectHighlightIndex(
                          selectableFilteredOptions.length,
                          current,
                          event.key === "ArrowDown" ? "next" : "previous",
                        ),
                      );
                      return;
                    }
                    if (event.key !== "Enter") return;
                    event.preventDefault();
                    const picked =
                      selectableFilteredOptions[highlightedIndex] ?? selectableFilteredOptions[0] ?? null;
                    if (picked == null) return;
                    onValueChange(picked);
                    setOpen(false);
                  }}
                  {...(floorScanTarget ? FLOOR_PAGE_SCAN_TARGET_PROPS : {})}
                />
              </div>
              <ul
                className="overflow-auto overscroll-y-contain py-1 text-sm touch-pan-y [-webkit-overflow-scrolling:touch]"
                style={{ maxHeight: panelBox.listContentMaxHeight }}
                onWheel={stopWheelFromClosingSelectPanel}
              >
                {isLoading ? (
                  <li className="px-3 py-2 text-slate-500">Loading…</li>
                ) : options.length === 0 ? (
                  <li className="px-3 py-2 text-slate-500">{emptyMessage}</li>
                ) : filteredOptions.length === 0 ? (
                  <li className="px-3 py-2 text-slate-500">{emptyMessage}</li>
                ) : (
                  filteredOptions.map((option) => {
                    if (isSearchableSelectDivider(option)) {
                      return <SearchableSelectDividerRow key={option.value} label={option.label} />;
                    }
                    if (option.disabled) {
                      return (
                        <li
                          key={option.value}
                          role="presentation"
                          className="border-t border-slate-200 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-400"
                        >
                          {option.label}
                        </li>
                      );
                    }
                    const isSelected = option.value === value;
                    const selectableIndex = selectableFilteredOptions.findIndex(
                      (candidate) => candidate.value === option.value,
                    );
                    const isHighlighted = selectableIndex === highlightedIndex;
                    return (
                      <li key={option.value} role="presentation">
                        <button
                          type="button"
                          role="option"
                          id={searchableSelectOptionDomId(listboxId, selectableIndex)}
                          aria-selected={isSelected || isHighlighted}
                          className={cn(
                            "flex min-h-12 w-full items-center justify-between gap-2 px-3 py-2 text-left text-base touch-manipulation hover:bg-slate-100",
                            isSelected && "bg-slate-50 font-medium",
                            isHighlighted && "bg-sky-50 ring-1 ring-inset ring-sky-200",
                          )}
                          onPointerDown={(event) => {
                            touchListPick.onListItemPointerDown(event, () => {
                              onValueChange(option);
                              setOpen(false);
                            });
                          }}
                        >
                          <span className="flex min-w-0 flex-col gap-0.5 text-left">
                            <span className={cn("whitespace-nowrap", option.className)}>{option.label}</span>
                            {option.description ? (
                              <span className="whitespace-normal text-xs font-normal leading-snug text-slate-500">
                                {option.description}
                              </span>
                            ) : null}
                          </span>
                          {optionTrailing ? (
                            <span className="shrink-0 self-start">{optionTrailing(option)}</span>
                          ) : null}
                        </button>
                      </li>
                    );
                  })
                )}
              </ul>
              {panelFooter != null ? (
                <div className="border-t border-slate-100 p-2">{panelFooter({ close: () => setOpen(false) })}</div>
              ) : null}
            </div>,
            portalTarget,
          )
        : null}
    </div>
  );
}

export function SearchableSelect(props: SearchableSelectProps) {
  const presentation = props.presentation ?? "panelSearch";
  const { autoFocus = false, presentation: _presentationConsumed, ...shared } = props;
  void _presentationConsumed;

  if (presentation === "inlineFilter") {
    return <SearchableSelectInlineFilter {...shared} autoFocus={autoFocus} />;
  }

  return <SearchableSelectPanelSearch {...shared} />;
}
